import { useSearchParams } from 'react-router-dom';
import { site } from '@shared/site.config';
import { useTitle } from '@/lib/useTitle';
import ContactForm from '@/components/ContactForm';
import type { LeadInterest } from '@shared/types';

const VALID_INTERESTS: LeadInterest[] = ['buying-selling', 'leasing', 'business-listing', 'general'];

export default function Contact() {
  useTitle(
    'Contact',
    `Reach ${site.agent.name} directly — phone, email, or the form. Every message goes to ${site.agent.shortName}, not a team inbox.`,
  );

  const [searchParams] = useSearchParams();
  const requested = searchParams.get('interest');
  const defaultInterest = VALID_INTERESTS.includes(requested as LeadInterest)
    ? (requested as LeadInterest)
    : 'general';

  return (
    <section className="relative border-b border-medline bg-grid-sheet">
      <span className="sheet-no absolute right-5 top-5 sm:right-8">A-05</span>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
        <div className="grid gap-14 lg:grid-cols-[1fr_480px] items-start">
          <div className="animate-fade-in">
            <p className="annot">Contact</p>
            <h1 className="mt-4 font-display text-[42px] font-semibold leading-[1.0] tracking-tight sm:text-[58px]">
              You'll be talking
              <br />
              to {site.agent.shortName}
            </h1>
            <p className="mt-6 max-w-lg text-[17px] leading-relaxed text-annot">
              Not an assistant, not an inside sales agent, not a lead-routing platform that sold your details to
              four other brokerages before the page finished loading.
            </p>

            {/* Contact data as a title-block-style grid. */}
            <div className="mt-10 grid grid-cols-1 gap-px border border-medline bg-medline sm:grid-cols-2">
              <div className="bg-sheet px-5 py-5">
                <p className="annot">Phone</p>
                <a
                  href={`tel:${site.agent.phone.replace(/\D/g, '')}`}
                  className="mt-2 block font-display text-[26px] leading-none text-redline hover:text-redline-600"
                >
                  {site.agent.phone}
                </a>
              </div>
              <div className="bg-sheet px-5 py-5">
                <p className="annot">Areas</p>
                <p className="mt-2 text-[15px] leading-snug text-graphite-600">
                  {site.serviceAreas.map((a) => a.name).join(' · ')}
                </p>
              </div>
              <div className="bg-sheet px-5 py-5">
                <p className="annot">Brokerage</p>
                <p className="mt-2 text-[15px] leading-snug text-graphite-600">
                  {site.agent.brokerage}
                  <span className="mt-0.5 block text-[13px] text-annot">
                    {site.agent.brokerageCity}
                    {site.agent.registration && ` · ${site.agent.registration}`}
                  </span>
                </p>
              </div>
            </div>

            <div className="mt-8 max-w-lg border-l-2 border-medline pl-5">
              <p className="annot">What happens to your details</p>
              <p className="mt-2 text-[13px] leading-relaxed text-annot">
                They're stored on this site's own server and emailed to {site.agent.name}. They are not sold,
                shared with a lead aggregator, or added to a marketing list.
              </p>
            </div>
          </div>

          <ContactForm
            defaultInterest={defaultInterest}
            title="Send a message"
            subtitle={`Goes straight to ${site.agent.shortName} — no routing, no queue.`}
          />
        </div>
      </div>
    </section>
  );
}
